import React from "react"
import { produce } from "immer"
import { set } from "lodash"

import { FormError, FormRule } from "../../models/form"
import { isNotEmpty } from "../../utils"

/*
	lodashSetFormFields
	Wrapper function that uses the lodash set function to allow setting state
	with a givenpath (eg "user.description.phone")
*/
export const lodashSetFormFields = (
	changes: { [key: string]: unknown },
	setFormFields?: React.Dispatch<React.SetStateAction<any>>
) => {
	setFormFields(
		// React change detection is based on Immutability, meaning that React doesnt change
		// when an object property has changed (mutation) but when the whole object changed.
		// Immer "produce" method allow use to be sure that the new returned state is immutable
		// See https://immerjs.github.io/immer/example-setstate
		produce((draft: any) => {
			Object.entries(changes).map(([key, value]) => {
				// Lodash "set" method allows to mutate an object with a new value at the
				// path given (eg "user.description.phone").
				// See https://lodash.com/docs#set
				return set(draft as Record<string, unknown>, key, value)
			})
		})
	)
}

export type HandleEvent = (event: React.ChangeEvent<HTMLInputElement>) => void

/*
	HandleInputValidation
	Generic function that get an array of errors of a given named input
	and store it in the form state so the form can be aware of all
	the errors of its input children
*/
export type HandleInputValidation = (
	name: string,
	errs: Array<FormError>,
	setErrors?: React.Dispatch<React.SetStateAction<Array<FormError>>>
) => void

export const handleInputValidation: HandleInputValidation = (name, errs, setErrors) => {
	setErrors((s: Array<FormError>) => {
		const cleanErrors = [...s.filter((e: FormError) => e.origin !== name)]
		return [...cleanErrors, ...errs]
	})
}

/*
	HandleInternalInputValidation
	Generic function that get an array of errors of a given named input
	and store it in the form state so the form can be aware of all
	the errors of its input children
*/
export type HandleInternalInputValidation = (
	currentValue: any,
	name: string,
	rules: FormRule[],
	setErrors: React.Dispatch<React.SetStateAction<Array<FormError>>>,
	onValidate: HandleInputValidation
) => void

export const handleInternalInputValidation: HandleInternalInputValidation = async (
	currentValue,
	name,
	rules,
	setErrors,
	onValidate
) => {
	const newErrors: React.SetStateAction<FormError[]> = []

	for (const rule of rules) {
		let isError = false
		// We use promise resolve to ensure that async custom rules are handled correctly
		// Even if the custom rule is not async, we treat it as such.
		await Promise.resolve((isError = !(await rule.check(currentValue)))).then(() => {
			if (isError) {
				newErrors.push({
					origin: name,
					rule: rule.key,
					message: rule.message,
				})
			}
		})
	}
	setErrors(newErrors)

	if (onValidate) onValidate(name, newErrors)
}

/*
	HandleInputChange
	Generic function that get the change event of any input
	and save it back in a given form fields state so the form
	can be aware of all the values of its input children
*/
export type HandleInputChange = (
	event: React.ChangeEvent<HTMLInputElement>,
	formFields: any,
	setFormFields?: React.Dispatch<React.SetStateAction<any>>
) => void

export const handleInputChange: HandleInputChange = (event, setFormFields) => {
	const target = event.target
	const value = target.type === "checkbox" ? target.checked : target.value

	const name = target.name

	// Note the handleInputChange is adapted so it can update the leaf properties of
	// a nested formFields (eg. "user.description.phone"). Hence the use here of
	// lodash (set) and immerJs (produce).
	lodashSetFormFields({ [name]: value }, setFormFields)
}

export const handlePreventTabsValueAtEndOnInput = (event: React.ChangeEvent<HTMLInputElement>) => {
	if (!isNotEmpty(event.target.value)) return event
	event.target.value = event.target.value.replace(/\t$/, "")
	return event
}

/*
	formStatusEnum
	Enum of the different status a form can be in
*/
export const formStatusEnum = {
	ready: "ready",
	loading: "loading",
	readonly: "readonly",
	complete: "complete",
	errored: "errored",
	expired: "expired",
}

type FormStatusEnumKeys = keyof typeof formStatusEnum
export type FormStatusEnumValues = (typeof formStatusEnum)[FormStatusEnumKeys]
